import { NgClass, NgFor, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CheckoutPaymentMethodsComponentDef } from '@df/checkout/checkout-payment-methods.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Client } from '@df/core/service/client';
import { ApplePayFilterPipe } from '@df/module-apple-pay/apple-pay-filter.pipe';
import { ApplePayService } from '@df/module-apple-pay/apple-pay.service';
import { IfKlarnaPaymentDirective } from '@df/module-klarna/if-klarna-payment.directive';
import { Stripe } from '@df/module-stripe/stripe.namespace';
import { StripeService } from '@df/module-stripe/stripe.service';
import { RadioGroupDirective } from '@df/ui/form/radio/radio.component.def';
import { TranslationModule } from '@df/ui/translation/translation.module';
import sortBy from 'lodash-es/sortBy';
import { TPaymentIcon } from '../common/payment-icon/payment-icon.component';
import { BraintreeHostedFieldsComponent } from '../module/braintree/hosted-fields/braintree-hosted-fields.component';
import { KlarnaPaymentComponent } from '../module/klarna/klarna-payment.component';
import { MollieCardsPaymentComponent } from '../module/mollie/cards/mollie-cards-payment.component';
import { StripeCardComponent } from '../module/stripe/stripe-card.component';
import { StripeSavedCardComponent } from '../module/stripe/stripe-saved-card.component';
import { InputWrapBodyDirective } from '../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../ui/form/input-wrap.component';
import { RadioComponent } from '../ui/form/radio';
import { CheckoutFormComponent } from './checkout-form.component';
import { CheckoutStepComponent } from './checkout-step.component';
@Component({
    selector: 'checkout-payment-methods',
    templateUrl: './checkout-payment-methods.html',
    standalone: true,
    imports: [
        ApplePayFilterPipe,
        BraintreeHostedFieldsComponent,
        FormsModule,
        InputWrapComponent,
        InputWrapBodyDirective,
        IfKlarnaPaymentDirective,
        KlarnaPaymentComponent,
        MollieCardsPaymentComponent,
        NgClass,
        NgFor,
        NgIf,
        NgSwitch,
        NgSwitch,
        NgSwitchCase,
        RadioComponent,
        RadioGroupDirective,
        StripeCardComponent,
        StripeSavedCardComponent,
        TranslationModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckoutPaymentMethodsComponent extends CheckoutPaymentMethodsComponentDef {
    protected MOLLIE_APPLE_PAY = 'mollie_methods_applepay';

    private mobileMethodsOrder = ['livetest', 'stripe_payments_prapi', 'stripe_payments', 'paypal_express', 'klarna_pay_over_time'];

    private desktopMethodsOrder = ['livetest', 'stripe_payments', 'paypal_express', 'klarna_pay_over_time', 'stripe_payments_prapi'];

    private stripeService = inject(StripeService);

    /**
     * Class constructor
     */
    constructor(
        checkoutFormComponent: CheckoutFormComponent,
        checkoutStepComponent: CheckoutStepComponent,
        changeDetectorRef: ChangeDetectorRef,
        private applePayService: ApplePayService,
        private client: Client
    ) {
        super(checkoutFormComponent, checkoutStepComponent, changeDetectorRef);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.add(this.client.obs.breakpoint.subscribe(() => this.onCheckoutChange({ full: true })));
    }

    protected getMethodTitle(method: Checkout.IPaymentMethod): string {
        if (method.code === Stripe.PaymentMethod.StripePaymentsExpress) {
            const methods: string[] = [];
            if (this.stripeService.isExpressCheckoutApplePaySupported()) {
                methods.push('Apple Pay');
            }
            if (this.stripeService.isExpressCheckoutGooglePaySupported()) {
                methods.push('Google Pay');
            }

            return methods.join('/');
        }

        return method.title;
    }

    protected getStripeExpressCheckoutLogos(): TPaymentIcon[] {
        const logos: TPaymentIcon[] = [];

        if (this.stripeService.isExpressCheckoutApplePaySupported()) {
            logos.push('apple');
        }

        if (this.stripeService.isExpressCheckoutGooglePaySupported()) {
            logos.push('google');
        }

        return logos;
    }

    protected override validateSelectedMethod(): void {
        super.validateSelectedMethod();
        if (
            this.data.method === this.MOLLIE_APPLE_PAY &&
            (!this.applePayService.isAvailable || !this.applePayService.isSupportedVersion(3))
        ) {
            this.data.method = undefined;
        }
    }

    protected get disabled(): boolean {
        return this.saveStatus.busy;
    }

    protected override getMethods(): Checkout.IPaymentMethod[] {
        const methods = super.getMethods();
        const isMobile = this.client.sizeId < 2;
        const order = isMobile ? this.mobileMethodsOrder : this.desktopMethodsOrder;
        this.methods = sortBy(methods, method => order.indexOf(method.code as string) + 1 || 1000);
        return this.methods;
    }
}
